import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  getPushPermission,
  isPushSupported,
  pushNeedsPwaInstall,
  subscribeToPush,
} from "../../../utils/pushNotifications";
import { isStandaloneMode } from "../../../utils/pwaUtils";
import { useToast } from "../../ui";
import s from "./clientApp.module.css";

const DISMISS_KEY = "baysport_push_prompt_dismissed";
const SHOW_DELAY_MS = 480;

function PushBellIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.73 21a2 2 0 01-3.46 0"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function InstallPhoneIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect
        x="7"
        y="2.5"
        width="10"
        height="19"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path d="M11 18.5h2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <path
        d="M12 6v6l3 2"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ClientPushBanner() {
  const toast = useToast();
  const tenant = useSelector((state) => state.auth.tenant);
  const [visible, setVisible] = useState(false);
  const [needsPwa, setNeedsPwa] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === "1") return undefined;

    let timer;

    const maybeShow = async () => {
      if (pushNeedsPwaInstall()) {
        timer = window.setTimeout(() => {
          setNeedsPwa(true);
          setVisible(true);
        }, SHOW_DELAY_MS);
        return;
      }

      if (!isStandaloneMode()) return;

      if (!isPushSupported()) return;

      const perm = await getPushPermission();
      if (perm === "granted" || perm === "unsupported" || perm === "denied") {
        return;
      }

      timer = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    };

    maybeShow();
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [visible]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      if (Notification.permission === "granted") {
        localStorage.setItem(DISMISS_KEY, "1");
        setVisible(false);
        setBusy(false);
        document.body.style.overflow = "";
      } else if (busyRef.current) {
        setBusy(false);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
    setBusy(false);
    document.body.style.overflow = "";
  };

  const enable = async () => {
    if (busyRef.current) return;
    setBusy(true);
    try {
      if (!isPushSupported()) {
        throw new Error("Push не поддерживается в этом браузере");
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Разрешение на уведомления не получено");
      }

      localStorage.setItem(DISMISS_KEY, "1");
      setVisible(false);
      setBusy(false);
      document.body.style.overflow = "";
      toast.success("Уведомления включены");

      subscribeToPush().catch(() => {});
    } catch (err) {
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          "Не удалось включить уведомления",
      );
    } finally {
      setBusy(false);
      document.body.style.overflow = "";
    }
  };

  const clubName = tenant?.name?.trim() || "клуба";

  return (
    <div
      className={s.pushPromptOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="push-prompt-title"
      onClick={() => !busy && dismiss()}
    >
      <div
        className={s.pushPromptCard}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={
            needsPwa ? s.pushPromptIconWrapMuted : s.pushPromptIconWrap
          }
          aria-hidden
        >
          {needsPwa ? <InstallPhoneIcon /> : <PushBellIcon />}
        </div>

        {needsPwa ? (
          <>
            <h2 id="push-prompt-title" className={s.pushPromptTitle}>
              Установите приложение
            </h2>
            <p className={s.pushPromptLead}>
              На iPhone уведомления работают только в установленном PWA.
            </p>
            <ol className={s.pushPromptSteps}>
              <li>
                Нажмите <strong>Поделиться</strong> в Safari
              </li>
              <li>
                Выберите <strong>На экран «Домой»</strong>
              </li>
              <li>Откройте приложение с главного экрана</li>
            </ol>
            <button
              type="button"
              className={s.pushPromptPrimary}
              onClick={dismiss}
            >
              Понятно
            </button>
          </>
        ) : (
          <>
            <h2 id="push-prompt-title" className={s.pushPromptTitle}>
              Включить уведомления?
            </h2>
            <p className={s.pushPromptLead}>
              Получайте важное от {clubName} — даже когда приложение закрыто.
            </p>
            <ul className={s.pushPromptBenefits}>
              <li>Напоминания о тренировках</li>
              <li>Новости и соревнования</li>
              <li>Сообщения от тренера и клуба</li>
            </ul>
            <button
              type="button"
              className={s.pushPromptPrimary}
              onClick={enable}
              disabled={busy}
            >
              {busy ? "Подключаем…" : "Разрешить уведомления"}
            </button>
            <button
              type="button"
              className={s.pushPromptSecondary}
              onClick={dismiss}
              disabled={busy}
            >
              Не сейчас
            </button>
          </>
        )}
      </div>
    </div>
  );
}
